import { useState } from "react"
import { filterKeys, isEmptyArray } from "web/services/helpers"
import { FieldListItem, Reorderable, ViewFilters } from "web/components"
import { Input } from "web/components/Form/Controls"
import { handleMoveChildren } from "web/components/Reorderable/ReorderChildrenHelper"
import {
  TileFieldSelector,
  TileSizeSelectors,
  TileTableEditor,
} from "web/components/Tiles"
import SummarySettings from "web/components/SummarySettings/SummarySettings"
import TileWizardActions from "web/components/AddTileWizard/TileWizardActions"

export function TileTableSettingsModal({
  backButtonHandler,
  close,
  isTemplate,
  overview,
  tile,
  tileEntry,
}) {
  const [title, setTitle] = useState({ value: tile.name })
  const [tileWidth, setTileWidth] = useState({
    value: tileEntry.width,
  })
  const [tileHeight, setTileHeight] = useState({
    value: tileEntry.height,
  })

  const { group_field_id, group_field_key, handleUpdateTableSettings } = tile

  const hasFields = tile.fields.length > 0
  const hasNoGroupingToggle = !(group_field_id && group_field_key)
  const groupTotalsDisabledToggle = hasNoGroupingToggle
  const detailsDisabledToggle = hasNoGroupingToggle || !tile.show_group_totals

  function tableSettingsHaveValues() {
    const settingKeys = [
      "group_direction",
      "group_field_id",
      "group_field_key",
      "second_group_direction",
      "second_group_field_id",
      "second_group_field_key",
      "sort_direction",
      "sort_field_key",
    ]
    const filteredKeys = filterKeys(tile, settingKeys)
    const keysWithValues = Object.values(filteredKeys).filter((value) => value)
    return !isEmptyArray(keysWithValues)
  }

  const canResetTableSettings = tableSettingsHaveValues()

  function handleDeleteField(id) {
    tile.update({
      overview_tile: {
        tile_fields_attributes: [
          {
            id: id,
            _destroy: true,
          },
        ],
      },
    })
  }

  function handleMoveField(prevPos, newPos) {
    handleMoveChildren({
      prevPos: prevPos,
      newPos: newPos,
      mainObj: tile,
      movingObjs: tile.fields,
      idName: "tile_field_id",
      firstLayer: "overview_tile",
      secondLayer: "tile_fields_attributes",
    })
  }

  async function handleSave() {
    if (
      tileEntry?.height !== tileHeight.value ||
      tileEntry?.width !== tileWidth.value
    ) {
      await overview.update({
        [overview.type]: {
          tile_entries_attributes: [
            {
              id: tileEntry.id,
              height: tileHeight.value,
              width: tileWidth.value,
            },
          ],
        },
      })
    }
    await tile.update({
      overview_tile: {
        name: title.value.trim(),
      },
    })

    close()
  }

  return (
    <>
      <div className="modal-content-above-commands">
        <div className="grid gap-y-5">
          <Input label="Title" value={title} onChange={setTitle} />
          <TileSizeSelectors
            tileWidth={tileWidth}
            tileHeight={tileHeight}
            setTileHeight={setTileHeight}
            setTileWidth={setTileWidth}
          />
        </div>
        <div className="mb-6 mt-6 rounded-md border px-4 py-4 shadow-sm">
          <div className="mb-1 text-lg font-bold">Filters</div>
          <div className="mb-3">
            {overview.type === "viewable" && (
              <div className="text-sm italic">
                Filters applied to this table will override the existing
                filter(s) currently applied to your Overview
              </div>
            )}
            <ViewFilters
              subjectType={tile.tile_fields_subject_type}
              topicId={tile.topic?.id}
              filterConditions={tile.conditions}
              availableFields={tile.available_fields}
              handleAddCondition={(conditionToAdd) =>
                tile.handleAddCondition(conditionToAdd, tile.data)
              }
              handleChangeCondition={(condition, updatedConditionValue) =>
                tile.handleChangeCondition({
                  condition,
                  updatedConditionValue,
                })
              }
              handleDeleteCondition={(condition) =>
                tile.handleDeleteCondition(condition)
              }
              handleAddFieldToConditions={(field) =>
                tile.handleAddFieldToConditions(field)
              }
              containerClasses="my-2 md:my-3"
            />
          </div>
        </div>
        <div className="mt-6 rounded-md border px-4 py-4 shadow-sm">
          <div className="mb-1 text-lg font-bold">Fields</div>
          <div className="mb-3">
            <TileFieldSelector
              tile={tile}
              subjectType={tile.tile_fields_subject_type}
            />
          </div>
          <div className="space-between flex">
            <Reorderable
              id="field-set"
              onMoveItem={handleMoveField}
              itemListClass={"flex flex-wrap"}
              itemContainerClass={"cursor-pointer mt-2 mr-2"}
            >
              {tile.fields.map((field) => (
                <FieldListItem
                  key={field.id}
                  label={field.label}
                  fieldId={field.tile_field_id}
                  presentation={field.presentation}
                  presentations={field.presentations}
                  changeFieldPresentation={tile.changeFieldPresentation}
                  handleDeleteField={() =>
                    handleDeleteField(field.tile_field_id)
                  }
                />
              ))}
            </Reorderable>
          </div>
        </div>
        <div className="mt-6 rounded-md border px-4 py-4 shadow-sm">
          <SummarySettings
            showDetails={tile.show_details}
            showGrandTotals={tile.show_grand_totals}
            showGroupTotals={tile.show_group_totals}
            groupTotalsDisabled={groupTotalsDisabledToggle}
            detailsDisabled={detailsDisabledToggle}
            useAverages={tile.use_averages}
            updateTable={handleUpdateTableSettings}
          />
        </div>

        <div className="mt-6 rounded-md border px-4 py-4 shadow-sm">
          <div className="flex place-content-between">
            <div className="mb-1 text-lg font-bold">Preview</div>
            {canResetTableSettings && (
              <button
                className="text-sm text-blue-500 hover:text-blue-800 hover:underline"
                onClick={tile.handleResetTableSettings}
              >
                Reset
              </button>
            )}
          </div>
          {hasFields && (
            <TileTableEditor
              tile={tile}
              data={tile.data}
              showDummyData={isTemplate}
            />
          )}
        </div>
      </div>
      <TileWizardActions
        tile={tile}
        onSaveHandler={handleSave}
        backButtonHandler={backButtonHandler}
      />
    </>
  )
}
